(() => {
  const normalize = (value) => {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  };

  const splitTerms = (value) => {
    return String(value || "")
      .split(/\n|\||;/)
      .map((term) => normalize(term))
      .filter(Boolean);
  };

  const getHeaderHeight = () => {
    const selectors = [
      "sticky-header",
      ".header-wrapper",
      "header"
    ];

    for (const selector of selectors) {
      const header = document.querySelector(selector);

      if (!header) {
        continue;
      }

      const rect = header.getBoundingClientRect();
      const style = window.getComputedStyle(header);

      if (style.display !== "none" && rect.height > 0) {
        return Math.round(rect.height);
      }
    }

    return 0;
  };

  const updateHeaderHeight = () => {
    document.documentElement.style.setProperty(
      "--header-height",
      `${getHeaderHeight()}px`
    );
  };

  const getAvailableWidth = () => {
    return Math.min(
      window.innerWidth,
      document.documentElement.clientWidth,
      window.visualViewport?.width || window.innerWidth
    );
  };

  const getRawShopifySections = () => {
    return Array.from(document.querySelectorAll(".shopify-section"));
  };

  const isScannableSection = (section) => {
    if (!section) {
      return false;
    }

    if (
      section.querySelector("[data-share-page-nav]") ||
      section.classList.contains("share-page-anchor-nav-section")
    ) {
      return false;
    }

    const rect = section.getBoundingClientRect();
    const style = window.getComputedStyle(section);

    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      rect.height > 40
    );
  };

  const getSectionHeadingTexts = (section) => {
    const headingSelectors = [
      "h1",
      "h2",
      "h3",
      "h4",
      "[class*='eyebrow']",
      "[class*='title']",
      "[class*='heading']"
    ];

    const blockedSelectors = [
      "[data-share-page-nav]",
      "nav",
      "[role='navigation']",
      "button[data-share-nav-item]",
      ".share-page-nav__desktop",
      ".share-page-nav__floating",
      ".share-page-nav__overlay"
    ];

    const headingNodes = Array.from(
      section.querySelectorAll(headingSelectors.join(", "))
    ).filter((node) => {
      return !blockedSelectors.some((selector) => {
        return node.closest(selector);
      });
    });

    return headingNodes
      .map((node) => normalize(node.textContent))
      .filter(Boolean);
  };

  const findBySelector = (selector, scanSections) => {
    if (!selector) {
      return null;
    }

    try {
      const matchedElement = document.querySelector(selector);

      if (!matchedElement) {
        return null;
      }

      const matchedSection = matchedElement.closest(".shopify-section");

      if (
        matchedSection &&
        scanSections.includes(matchedSection)
      ) {
        return matchedSection;
      }

      if (!matchedSection) {
        return matchedElement;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const findByText = (terms, scanSections) => {
    if (!terms.length) {
      return null;
    }

    let bestMatch = null;
    let bestScore = 0;

    scanSections.forEach((section) => {
      const headingTexts = getSectionHeadingTexts(section);

      headingTexts.forEach((headingText) => {
        terms.forEach((term) => {
          let score = 0;

          if (headingText === term) {
            score = 100;
          } else if (headingText.includes(term)) {
            score = 80;
          } else if (
            term.includes(headingText) &&
            headingText.length > 4
          ) {
            score = 60;
          }

          if (score > bestScore) {
            bestScore = score;
            bestMatch = section;
          }
        });
      });
    });

    return bestMatch;
  };

  const findByOrder = (order, scanSections) => {
    const orderNumber = Number(order);

    if (!orderNumber || orderNumber < 1) {
      return null;
    }

    return scanSections[orderNumber - 1] || null;
  };

  const initSharePageNavigation = (root) => {
    if (!root || root.dataset.initialized === "true") {
      return;
    }

    root.dataset.initialized = "true";

    const desktopNav = root.querySelector("[data-share-nav-desktop]");
    const floatingButton = root.querySelector("[data-share-nav-floating]");
    const overlay = root.querySelector("[data-share-nav-overlay]");
    const closeButton = root.querySelector("[data-share-nav-close]");
    const currentNumber = root.querySelector("[data-share-nav-current-number]");
    const currentLabel = root.querySelector("[data-share-nav-current-label]");
    const navButtons = Array.from(
      root.querySelectorAll("[data-share-nav-item]")
    );

    let targets = [];
    let currentIndex = 0;
    let navigationMode = "compact";
    let overlayOpen = false;
    let previousOverflow = "";
    let scrollFrame = null;
    let resizeTimer = null;

    const getScanSections = () => {
      const scope = root.dataset.scanScope || "before";
      const ownSection = root.closest(".shopify-section");
      const allSections = getRawShopifySections();

      if (!ownSection) {
        return allSections.filter(isScannableSection);
      }

      const ownIndex = allSections.indexOf(ownSection);

      if (scope === "all" || ownIndex < 0) {
        return allSections.filter((section) => {
          return section !== ownSection && isScannableSection(section);
        });
      }

      if (scope === "after") {
        return allSections
          .slice(ownIndex + 1)
          .filter(isScannableSection);
      }

      return allSections
        .slice(0, ownIndex)
        .filter(isScannableSection);
    };

    const resolveTarget = (button, scanSections) => {
      const method = button.dataset.matchMethod || "auto";
      const terms = splitTerms(button.dataset.matchText);
      const selector = button.dataset.cssSelector;
      const order = button.dataset.sectionOrder;

      if (method === "selector") {
        return findBySelector(selector, scanSections);
      }

      if (method === "text") {
        return findByText(terms, scanSections);
      }

      if (method === "order") {
        return findByOrder(order, scanSections);
      }

      return (
        findBySelector(selector, scanSections) ||
        findByText(terms, scanSections) ||
        findByOrder(order, scanSections)
      );
    };

    const collectTargets = () => {
      const scanSections = getScanSections();
      const primaryButtons = navButtons.filter((button) => {
        return button.closest(".share-page-nav__desktop");
      });

      targets = primaryButtons
        .map((button, index) => {
          const target = resolveTarget(button, scanSections);
          const hideMissing = button.dataset.hideMissing === "true";

          navButtons
            .filter((item) => item.dataset.navIndex === String(index))
            .forEach((item) => {
              item.classList.toggle("is-hidden", !target && hideMissing);
            });

          return {
            index,
            target,
            label: button.dataset.label || "",
            number: button.dataset.number || "",
            found: Boolean(target)
          };
        })
        .filter((item) => item.found);
    };

    const determineMode = () => {
      const minWidth = Number(root.dataset.desktopMinWidth || 1380);
      const scale = window.visualViewport?.scale || 1;

      navigationMode =
        getAvailableWidth() >= minWidth && scale <= 1.08
          ? "desktop"
          : "compact";
    };

    const closeOverlay = () => {
      if (!overlay || !floatingButton) {
        return;
      }

      overlayOpen = false;
      document.documentElement.style.overflow = previousOverflow;

      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
      floatingButton.classList.remove("is-open");
      floatingButton.setAttribute("aria-expanded", "false");
    };

    const openOverlay = () => {
      if (!overlay || !floatingButton) {
        return;
      }

      overlayOpen = true;
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";

      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      floatingButton.classList.add("is-open");
      floatingButton.setAttribute("aria-expanded", "true");
    };

    const updateActive = () => {
      if (!targets.length) {
        desktopNav?.classList.remove("is-visible");
        floatingButton?.classList.remove("is-visible");
        return;
      }

      const headerOffset =
        getHeaderHeight() +
        Number(root.dataset.scrollOffset || 20) +
        90;

      let active = targets[0];

      targets.forEach((item) => {
        const rect = item.target.getBoundingClientRect();

        if (rect.top <= headerOffset) {
          active = item;
        }
      });

      currentIndex = active.index;

      navButtons.forEach((button) => {
        button.classList.toggle(
          "is-active",
          Number(button.dataset.navIndex) === currentIndex
        );
      });

      if (currentNumber) {
        currentNumber.textContent = active.number;
      }

      if (currentLabel) {
        currentLabel.textContent = active.label;
      }

      const pageHasScrolled =
        window.scrollY > Math.max(100, getHeaderHeight());

      const showDesktop =
        navigationMode === "desktop" && pageHasScrolled;

      const showFloating =
        navigationMode === "compact" && pageHasScrolled;

      desktopNav?.classList.toggle("is-visible", showDesktop);
      floatingButton?.classList.toggle("is-visible", showFloating);

      if (!showFloating && overlayOpen) {
        closeOverlay();
      }
    };

    const scrollToTarget = (index) => {
      const item = targets.find((target) => {
        return target.index === Number(index);
      });

      if (!item?.target) {
        return;
      }

      const offset =
        getHeaderHeight() +
        Number(root.dataset.scrollOffset || 20);

      const top =
        item.target.getBoundingClientRect().top +
        window.scrollY -
        offset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth"
      });
    };

    const handleScroll = () => {
      if (scrollFrame) {
        return;
      }

      scrollFrame = window.requestAnimationFrame(() => {
        updateActive();
        scrollFrame = null;
      });
    };

    const refresh = () => {
      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(() => {
        updateHeaderHeight();
        determineMode();
        collectTargets();
        updateActive();
      }, 100);
    };

    navButtons.forEach((button) => {
      button.addEventListener("click", () => {
        closeOverlay();
        scrollToTarget(button.dataset.navIndex);
      });
    });

    floatingButton?.addEventListener("click", () => {
      if (overlayOpen) {
        closeOverlay();
      } else {
        openOverlay();
      }
    });

    closeButton?.addEventListener("click", closeOverlay);

    overlay?.addEventListener("click", (event) => {
      if (event.target === overlay) {
        closeOverlay();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && overlayOpen) {
        closeOverlay();
      }
    });

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", refresh, { passive: true });

    window.visualViewport?.addEventListener("resize", refresh, {
      passive: true
    });

    window.visualViewport?.addEventListener("scroll", handleScroll, {
      passive: true
    });

    document.addEventListener("shopify:section:load", refresh);

    updateHeaderHeight();
    determineMode();
    collectTargets();
    updateActive();
  };

  const initAllSharePageNavigations = () => {
    document
      .querySelectorAll("[data-share-page-nav]")
      .forEach(initSharePageNavigation);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllSharePageNavigations);
  } else {
    initAllSharePageNavigations();
  }

  document.addEventListener(
    "shopify:section:load",
    initAllSharePageNavigations
  );
})();